// Utility functions for GPN Admin Agreement management and compliance tracking
import { getDisplayRoleName } from './roleUtils';

/**
 * Check if the given user has Super Admin authority.
 * Role ID 1 is Super Admin.
 */
export const isSuperAdmin = (user) => {
  if (!user) return false;
  const role = user?.role !== undefined ? user.role : (user?.role_id !== undefined ? user.role_id : null);
  if (role === 1 || role === '1') return true;

  const roleName = (user?.role_name || user?.role?.name || (typeof user?.role === 'string' ? user?.role : '')).toLowerCase();
  return roleName.includes('super admin');
};

export const CURRENT_AGREEMENT_VERSION = '2.4.0';

export const getAgreementStorageKey = (user) => {
  const identifier = user?.id || user?._id || user?.email || 'current_admin';
  return `gpn_admin_agreement_${identifier}`;
};

/**
 * Checks if the user has completed the admin agreement.
 * NOTE: Super Admin is completely exempt from the admin agreement requirement (unless switched into org view).
 * If a new agreement version is introduced later, the admin should be required to accept the new version before accessing the portal again.
 */
export const isAgreementCompleted = (user) => {
  // Super admin does NOT need this agreement unless in switched org view as regular admin
  if (isSuperAdmin(user) && !user?.is_switched_view) {
    return true;
  }

  // Check localStorage for current version acceptance
  try {
    const key = getAgreementStorageKey(user);
    const stored = localStorage.getItem(key);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Strict version check (Requirement 5)
      if (parsed?.accepted && parsed?.version === CURRENT_AGREEMENT_VERSION) {
        return true;
      }
    }
  } catch (e) {
    console.error('Error reading admin agreement status from localStorage:', e);
  }

  // Check user object from backend if available and version matches
  if ((user?.admin_agreement_accepted || user?.agreement_accepted) && 
      (user?.admin_agreement_version === CURRENT_AGREEMENT_VERSION || !user?.admin_agreement_version)) {
    return true;
  }

  return false;
};

/**
 * Check if an administrator is required to complete the agreement and has not yet done so.
 * Returns false for Super Admin.
 */
export const needsAdminAgreement = (user) => {
  if (!user) return false;
  if (isSuperAdmin(user) && !user?.is_switched_view) return false;
  return !isAgreementCompleted(user);
};

/**
 * Calculates the 2-day (48-hour) agreement acceptance period.
 * Requirement 5:
 * When an admin is required to accept an agreement, provide a 2-day period for acceptance.
 * If not accepted within 2 days, continue restricting portal access until accepted.
 */
export const getAgreementGracePeriodInfo = (user) => {
  if (!user || (isSuperAdmin(user) && !user?.is_switched_view)) {
    return { isRequired: false, isExpired: false, hoursRemaining: 0, minutesRemaining: 0 };
  }

  const identifier = user?.id || user?._id || user?.email || 'admin';
  const reqKey = `gpn_agreement_required_timestamp_${identifier}`;
  let requiredTimestamp = localStorage.getItem(reqKey);

  if (!requiredTimestamp) {
    // If first time encountering agreement requirement, record current timestamp or user created_at
    requiredTimestamp = user?.created_at ? new Date(user.created_at).toISOString() : new Date().toISOString();
    localStorage.setItem(reqKey, requiredTimestamp);
  }

  const startMs = new Date(requiredTimestamp).getTime();
  const periodMs = 2 * 24 * 60 * 60 * 1000; // 48 hours = 2 days
  const deadlineMs = startMs + periodMs;
  const nowMs = Date.now();
  const remainingMs = deadlineMs - nowMs;
  const isExpired = remainingMs <= 0;

  const totalHours = Math.max(0, Math.floor(remainingMs / (1000 * 60 * 60)));
  const totalMinutes = Math.max(0, Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60)));

  return {
    isRequired: true,
    requiredAt: requiredTimestamp,
    deadlineAt: new Date(deadlineMs).toISOString(),
    isExpired,
    hoursRemaining: totalHours,
    minutesRemaining: totalMinutes,
    totalRemainingMs: remainingMs,
    version: CURRENT_AGREEMENT_VERSION
  };
};

export const getAgreementDetails = (user) => {
  try {
    const key = getAgreementStorageKey(user);
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Error reading admin agreement details:', e);
  }
  return null;
};

/**
 * Centralized registry of all admin agreement acceptance states.
 */
export const getAgreementsRegistry = () => {
  try {
    const raw = localStorage.getItem('gpn_admin_agreements_registry');
    if (raw) {
      return JSON.parse(raw);
    }
  } catch (e) {
    console.error('Error reading agreements registry:', e);
  }
  return {};
};

export const saveAgreementCompletion = (user, details = {}) => {
  try {
    const key = getAgreementStorageKey(user);
    const adminId = String(user?.id || user?._id || details.adminId || 'admin_' + Date.now());
    const adminEmail = details.adminEmail || user?.email || '';
    const adminName = details.adminName || user?.name || user?.username || 'Administrator';
    const organization = details.organization || user?.organization || 'Glocal Prayer Network';
    const role = details.role || user?.role || 2;
    const roleName = details.roleName || (user?.role === 2 ? 'Organization Admin' : user?.role === 3 ? 'Organization Staff' : 'Group Admin');

    const record = {
      accepted: true,
      acceptedAt: new Date().toISOString(),
      adminId,
      adminName,
      adminEmail,
      organization,
      role,
      roleName,
      signature: details.signature || adminName || 'Admin Signature',
      signatureType: details.signatureType || 'digital_draw', // 'digital_draw' | 'paper_upload' | 'typed'
      signatureImage: details.signatureImage || null, // Base64 image URL of digital canvas or white paper photo
      paperFileName: details.paperFileName || null, // File name of uploaded white paper photo
      paperFileSize: details.paperFileSize || null,
      verificationCode: details.verificationCode || `GPN-AGR-${new Date().getFullYear()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`,
      version: CURRENT_AGREEMENT_VERSION,
    };

    localStorage.setItem(key, JSON.stringify(record));

    // Also update centralized registry for Super Admin tracking
    const registry = getAgreementsRegistry();
    const registryKey = adminId || adminEmail;
    registry[registryKey] = record;
    if (adminEmail) registry[adminEmail] = record;
    localStorage.setItem('gpn_admin_agreements_registry', JSON.stringify(registry));

    // Dispatch global event for live reactive update across the UI
    window.dispatchEvent(new CustomEvent('gpn_admin_agreement_updated', { detail: record }));
    return record;
  } catch (e) {
    console.error('Error saving admin agreement completion:', e);
    return null;
  }
};

export const resetAgreementCompletion = (user) => {
  try {
    const key = getAgreementStorageKey(user);
    localStorage.removeItem(key);

    const adminId = String(user?.id || user?._id || '');
    const adminEmail = user?.email || '';

    const registry = getAgreementsRegistry();
    if (adminId && registry[adminId]) delete registry[adminId];
    if (adminEmail && registry[adminEmail]) delete registry[adminEmail];
    localStorage.setItem('gpn_admin_agreements_registry', JSON.stringify(registry));

    window.dispatchEvent(new CustomEvent('gpn_admin_agreement_updated', { detail: { accepted: false, user } }));
  } catch (e) {
    console.error('Error resetting admin agreement status:', e);
  }
};

/**
 * Super Admin action: manually update an administrator's agreement status (accept or revoke),
 * including recording their digital signature or uploaded white paper photo.
 */
export const updateAdminAgreementStatusBySuperAdmin = (adminRecord, newStatus, extraDetails = {}) => {
  try {
    const registry = getAgreementsRegistry();
    const adminId = String(adminRecord.id || adminRecord.adminId || adminRecord.email);
    const adminEmail = adminRecord.email || adminRecord.adminEmail;

    const updated = {
      ...adminRecord,
      accepted: newStatus,
      acceptedAt: newStatus ? (extraDetails.acceptedAt || adminRecord.acceptedAt || new Date().toISOString()) : null,
      verificationCode: newStatus ? (extraDetails.verificationCode || adminRecord.verificationCode || `GPN-AGR-${new Date().getFullYear()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`) : null,
      signature: newStatus ? (extraDetails.signature || adminRecord.signature || adminRecord.name) : null,
      signatureType: newStatus ? (extraDetails.signatureType || adminRecord.signatureType || 'paper_upload') : null,
      signatureImage: newStatus ? (extraDetails.signatureImage || adminRecord.signatureImage || null) : null,
      paperFileName: newStatus ? (extraDetails.paperFileName || adminRecord.paperFileName || null) : null,
      updatedBy: 'Super Admin',
      updatedAt: new Date().toISOString(),
    };

    registry[adminId] = updated;
    if (adminEmail) registry[adminEmail] = updated;

    localStorage.setItem('gpn_admin_agreements_registry', JSON.stringify(registry));
    window.dispatchEvent(new CustomEvent('gpn_admin_agreement_updated', { detail: updated }));
    return updated;
  } catch (e) {
    console.error('Error updating admin agreement by Super Admin:', e);
    return null;
  }
};

/**
 * Default administrative accounts for fallback demonstration if completely offline.
 */
export const DEFAULT_ADMIN_USERS = [
  {
    id: 'adm_101',
    name: 'Pastor David Miller',
    email: 'david.miller@gracehope.org',
    role: 2,
    roleName: 'Organization Admin',
    organization: 'Grace Hope Community Church',
    phone: '+1 (555) 234-5678',
    accepted: true,
    acceptedAt: '2026-09-12T10:30:00.000Z',
    signature: 'Pastor David Miller',
    signatureType: 'digital_draw',
    verificationCode: 'GPN-AGR-2026-DH827A',
  },
  {
    id: 'adm_102',
    name: 'Sister Sarah Jenkins',
    email: 'sarah.jenkins@livingwaters.net',
    role: 3,
    roleName: 'Organization Staff',
    organization: 'Living Waters Fellowship',
    phone: '+1 (555) 345-6789',
    accepted: true,
    acceptedAt: '2026-09-18T14:15:00.000Z',
    signature: 'Sarah Jenkins',
    signatureType: 'paper_upload',
    verificationCode: 'GPN-AGR-2026-SJ993C',
  },
];

/**
 * Builds the compliance list for Super Admin tracking.
 * When real backend users are returned from the database, it returns EXCLUSIVELY the exact real data!
 * Super Admin (role 1) is exempt and excluded from signing.
 * All other users are tracked.
 */
export const getMergedAdminComplianceList = (backendUsers = []) => {
  const registry = getAgreementsRegistry();
  const list = [];
  const processedKeys = new Set();

  // If real backend users are passed from the API: USE 100% REAL DATA ONLY!
  if (Array.isArray(backendUsers) && backendUsers.length > 0) {
    backendUsers.forEach((u) => {
      const roleId = u?.role !== undefined 
        ? Number(u.role) 
        : (u?.role_id !== undefined 
          ? Number(u.role_id) 
          : (u?.roles?.id ? Number(u.roles.id) : 0));

      // Skip Super Admin (role 1) because Super Admin is exempt from submitting the agreement
      if (roleId === 1) return;

      const appUser = u?.app_users || u;
      const id = String(u?.id || appUser?.id || u?._id || '');
      const email = appUser?.email || u?.email || '';
      const firstName = appUser?.first_name || u?.first_name || '';
      const lastName = appUser?.last_name || u?.last_name || '';
      const name = `${firstName} ${lastName}`.trim() || appUser?.name || u?.name || 'Administrator';
      const orgName = u?.origanisation?.org_name || u?.organizations?.name || u?.organization?.name || u?.organization || 'Christian Organization';

      const registryRecord = registry[id] || (email ? registry[email] : null);
      const isAccepted = Boolean(registryRecord?.accepted || u?.admin_agreement_accepted || u?.agreement_accepted);

      let roleName = getDisplayRoleName(roleId);
      if (!roleName) {
        if (roleId === 2) roleName = 'Organization Admin';
        else if (roleId === 3) roleName = 'Organization Staff';
        else if (roleId === 5) roleName = 'Group Admin';
        else if (roleId === 4) roleName = 'App User';
        else roleName = `Role ${roleId}`;
      }

      const item = {
        id: id || email,
        name,
        email,
        phone: appUser?.phone || appUser?.phone_no || u?.phone || '',
        role: roleId,
        roleName,
        organization: orgName,
        accepted: isAccepted,
        acceptedAt: registryRecord?.acceptedAt || (isAccepted ? '2026-09-10T12:00:00Z' : null),
        signature: registryRecord?.signature || (isAccepted ? name : null),
        signatureType: registryRecord?.signatureType || (isAccepted ? 'digital_draw' : null),
        signatureImage: registryRecord?.signatureImage || null,
        paperFileName: registryRecord?.paperFileName || null,
        verificationCode: registryRecord?.verificationCode || (isAccepted ? `GPN-AGR-2026-${(id || '0000').slice(-4).toUpperCase()}` : null),
      };

      if (id) processedKeys.add(id);
      if (email) processedKeys.add(email);
      list.push(item);
    });

    // Return ONLY the real users from database! ZERO fake users!
    return list;
  }

  // Only if no backend users are present (e.g. offline mock demo mode)
  const isDemo = typeof window !== 'undefined' && (
    window.location.search.includes('auth=demo') ||
    window.location.search.includes('demo=true') ||
    window.location.search.includes('privacy=true') ||
    localStorage.getItem('gpn_demo_mode') === 'true' ||
    localStorage.getItem('token') === 'gpn_demo_super_admin_token'
  );

  if (isDemo) {
    return DEFAULT_ADMIN_USERS.map((admin) => {
      const reg = registry[admin.id] || registry[admin.email];
      return reg ? { ...admin, ...reg } : admin;
    });
  }

  return [];
};
